/* eslint-disable react/no-unescaped-entities */
/**
 * Admin — Module Fiches Spécialistes (alimentation manuelle).
 *
 * Cas d'usage :
 *  - Ajouter/modifier/suspendre/archiver des fiches spécialiste
 *  - Recherche par nom, profession, ville, spécialité
 *  - Import .xlsx en masse (template téléchargeable)
 *  - Saisir le score admin (1-100, invisible au public)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { apiClient, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Plus, Upload, Download, Pencil, Pause, Play, Archive,
  X, Star, Award, ShieldCheck, Loader2, FileSpreadsheet, AlertCircle,
} from "lucide-react";

const PROFESSIONS = [
  "avocat", "notaire", "expert-comptable", "cgp",
  "commissaire-de-justice", "mediateur", "administrateur-judiciaire",
  "mandataire-judiciaire", "spécialiste-immobilier", "fiduciaire",
  "huissier", "conseil-fiscal", "autre",
];
const AVAILABILITIES = [
  { value: "immediate", label: "Immédiate" },
  { value: "24h", label: "Sous 24h" },
  { value: "48h", label: "Sous 48h" },
  { value: "rdv", label: "Sur rendez-vous" },
];
const STATUSES = [
  { value: "active", label: "Actif", tone: "emerald" },
  { value: "suspended", label: "Suspendu", tone: "rose" },
  { value: "pending", label: "En attente", tone: "amber" },
];

const EMPTY_FORM = {
  first_name: "", last_name: "", photo: "",
  profession: "avocat", barreau: "",
  city: "", zone_intervention: "",
  specialties: "", sub_specialties: "",
  presentation_courte: "", presentation_detaillee: "",
  phone: "", email: "", website: "",
  languages: "Français", experience_years: "",
  diplomas: "",
  subscription_status: "active", is_referenced: true, is_premium: false,
  display_priority: 0, admin_score: 50,
  presentation_video: "", availability: "rdv",
  admin_notes: "",
};

function statusBadge(s) {
  const cfg = STATUSES.find(x => x.value === s) || { label: s, tone: "slate" };
  const tones = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tones[cfg.tone]}`}>{cfg.label}</span>;
}

function toCSV(arr) { return (arr || []).join("; "); }
function fromCSV(str) { return String(str || "").split(/[;,\n]/).map(s => s.trim()).filter(Boolean); }

export default function AdminExpertsManage() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ active_or_pending: 0, archived: 0, suspended: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [profession, setProfession] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("manual");
  const [editing, setEditing] = useState(null); // null | "new" | expert_id
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (profession) params.profession = profession;
      if (city) params.city = city;
      if (status) params.status = status;
      if (source) params.source = source;
      const { data } = await apiClient.get("/admin/experts/manual", { params });
      setItems(data.items || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error("Chargement impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, profession, city, status, source]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing("new"); };
  const openEdit = async (id) => {
    setBusy(true);
    try {
      const { data } = await apiClient.get(`/admin/experts/manual/${id}`);
      setForm({
        ...EMPTY_FORM,
        ...data,
        zone_intervention: toCSV(data.zone_intervention),
        specialties: toCSV(data.specialties),
        sub_specialties: toCSV(data.sub_specialties),
        languages: toCSV(data.languages),
        diplomas: toCSV(data.diplomas),
        experience_years: data.experience_years ?? "",
        admin_score: data.admin_score ?? 50,
        display_priority: data.display_priority ?? 0,
      });
      setEditing(id);
    } catch (e) {
      toast.error("Lecture fiche impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const close = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        zone_intervention: fromCSV(form.zone_intervention),
        specialties: fromCSV(form.specialties),
        sub_specialties: fromCSV(form.sub_specialties),
        languages: fromCSV(form.languages),
        diplomas: fromCSV(form.diplomas),
        experience_years: form.experience_years === "" ? null : Number(form.experience_years),
        admin_score: Number(form.admin_score),
        display_priority: Number(form.display_priority),
        email: form.email || null,
      };
      if (editing === "new") {
        await apiClient.post("/admin/experts/manual", payload);
        toast.success("Fiche créée");
      } else {
        await apiClient.put(`/admin/experts/manual/${editing}`, payload);
        toast.success("Fiche mise à jour");
      }
      close();
      await load();
    } catch (e) {
      toast.error("Enregistrement impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const doAction = async (id, action) => {
    if (action === "archive" && !window.confirm("Archiver cette fiche ?")) return;
    setBusy(true);
    try {
      await apiClient.post(`/admin/experts/manual/${id}/${action}`);
      toast.success({ suspend: "Fiche suspendue", restore: "Fiche réactivée", archive: "Fiche archivée" }[action]);
      await load();
    } catch (e) {
      toast.error("Action impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    // Auth par cookie HttpOnly → ouvrir dans une nouvelle fenêtre suffit.
    window.open(`${API}/admin/experts/manual/template.xlsx`, "_blank");
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await apiClient.post("/admin/experts/manual/import-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const c = data.created?.length || 0;
      const er = data.errors?.length || 0;
      if (er === 0) toast.success(`${c} fiche(s) importée(s)`);
      else toast.warning(`${c} créée(s), ${er} erreur(s)`, { description: data.errors?.[0]?.error });
      await load();
    } catch (err) {
      toast.error("Import impossible", { description: formatApiError(err?.response?.data?.detail) });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar variant="light" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* En-tête */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] font-medium mb-2">Administration</p>
            <h1 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] tracking-tight">Fiches Spécialistes</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Alimentation manuelle du réseau, score admin interne, import Excel. Les fiches créées ici apparaissent dans l'orientation et le matching CONFILEX.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="btn-download-template"
              className="border-slate-300">
              <Download className="w-4 h-4 mr-1.5" /> Template Excel
            </Button>
            <Button variant="outline" disabled={importing} onClick={() => fileInputRef.current?.click()} data-testid="btn-import-xlsx"
              className="border-slate-300">
              {importing
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Upload className="w-4 h-4 mr-1.5" />}
              Importer .xlsx
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx" onChange={importFile} className="hidden"
              data-testid="input-import-xlsx" />
            <Button onClick={openNew} data-testid="btn-new-expert"
              className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Ajouter un spécialiste
            </Button>
          </div>
        </div>

        {/* Stats compactes */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Fiches manuelles actives</p>
            <p className="text-2xl font-serif text-[#0B3A2A] mt-1" data-testid="stat-active">{totals.active_or_pending}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Suspendues</p>
            <p className="text-2xl font-serif text-amber-700 mt-1" data-testid="stat-suspended">{totals.suspended}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Archivées</p>
            <p className="text-2xl font-serif text-slate-500 mt-1" data-testid="stat-archived">{totals.archived}</p>
          </div>
        </div>

        {/* Filtres */}
        <div className="rounded-xl bg-white border border-slate-200 p-4 mb-6">
          <div className="grid md:grid-cols-5 gap-3">
            <div className="relative md:col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input data-testid="filter-q" placeholder="Nom, prénom, email, ville…"
                value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
            </div>
            <select data-testid="filter-profession" value={profession} onChange={e => setProfession(e.target.value)}
              className="rounded-md border border-slate-300 px-3 text-sm bg-white">
              <option value="">Toutes professions</option>
              {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <Input data-testid="filter-city" placeholder="Ville…" value={city} onChange={e => setCity(e.target.value)} />
            <select data-testid="filter-status" value={status} onChange={e => setStatus(e.target.value)}
              className="rounded-md border border-slate-300 px-3 text-sm bg-white">
              <option value="">Tous statuts</option>
              <option value="active">Actifs</option>
              <option value="suspended">Suspendus</option>
              <option value="pending">En attente</option>
              <option value="archived">Archivés</option>
            </select>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <span className="text-slate-500">Source :</span>
            {[{ v: "manual", l: "Fiches manuelles" }, { v: "applied", l: "Candidatures" }, { v: "", l: "Toutes" }].map(o => (
              <button key={o.v} onClick={() => setSource(o.v)}
                data-testid={`filter-source-${o.v || "all"}`}
                className={`px-3 py-1 rounded-full border transition ${source === o.v
                  ? "bg-[#0B3A2A] text-white border-[#0B3A2A]"
                  : "bg-white text-slate-700 border-slate-300 hover:border-[#C9A961]"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Tableau */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              Aucune fiche pour ces critères. Importez ou ajoutez-en une.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="experts-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Spécialiste</th>
                  <th className="px-4 py-3">Profession</th>
                  <th className="px-4 py-3">Ville</th>
                  <th className="px-4 py-3">Spécialités</th>
                  <th className="px-4 py-3">Score admin</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {e.photo
                          ? <img src={e.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                          : <div className="w-8 h-8 rounded-full bg-[#FAF7F1] border border-[#C9A961]/30 flex items-center justify-center font-serif text-[#0B3A2A]">
                              {(e.first_name?.[0] || "?")}{(e.last_name?.[0] || "")}
                            </div>}
                        <div>
                          <div className="text-[#0B3A2A] font-medium">{e.first_name} {e.last_name}</div>
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mt-0.5">
                            {e.is_premium && <Award className="w-3 h-3 text-[#C9A961]" />}
                            {e.verified && <ShieldCheck className="w-3 h-3 text-emerald-600" />}
                            {e.source === "manual" && <Badge variant="outline" className="text-[9px] px-1 py-0">manuel</Badge>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-700">{e.profession}</td>
                    <td className="px-4 py-3 text-slate-700">{e.city}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs max-w-[200px] truncate">
                      {(e.specialties || []).slice(0, 2).join(", ")}
                      {(e.specialties || []).length > 2 && ` +${e.specialties.length - 2}`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3 h-3 text-[#C9A961]" />
                        <span className="font-medium text-[#0B3A2A]">{e.admin_score ?? "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{statusBadge(e.subscription_status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(e.id)} disabled={busy}
                          title="Éditer" data-testid={`act-edit-${e.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-[#0B3A2A]">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {e.subscription_status === "suspended"
                          ? <button onClick={() => doAction(e.id, "restore")} disabled={busy}
                              title="Réactiver" data-testid={`act-restore-${e.id}`}
                              className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700">
                              <Play className="w-3.5 h-3.5" />
                            </button>
                          : <button onClick={() => doAction(e.id, "suspend")} disabled={busy}
                              title="Suspendre" data-testid={`act-suspend-${e.id}`}
                              className="p-1.5 rounded hover:bg-amber-50 text-amber-700">
                              <Pause className="w-3.5 h-3.5" />
                            </button>
                        }
                        <button onClick={() => doAction(e.id, "archive")} disabled={busy}
                          title="Archiver" data-testid={`act-archive-${e.id}`}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-700">
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Drawer édition */}
      {editing && (
        <ExpertDrawer
          mode={editing === "new" ? "create" : "edit"}
          form={form} setForm={setForm}
          onClose={close} onSave={save} saving={busy}
        />
      )}

      <Footer />
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700 mb-1 inline-flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {children}
      {hint && <span className="text-[10px] text-slate-400 mt-0.5 block">{hint}</span>}
    </label>
  );
}

function ExpertDrawer({ mode, form, setForm, onClose, onSave, saving }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="expert-drawer">
      <button onClick={onClose} aria-label="Fermer" className="flex-1 bg-black/40 backdrop-blur-sm" />
      <div className="w-full md:w-[720px] max-w-full bg-white h-full overflow-y-auto shadow-2xl border-l border-slate-200">
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#C9A961]">{mode === "create" ? "Nouvelle fiche" : "Édition"}</p>
            <h2 className="font-serif text-xl text-[#0B3A2A]">
              {form.first_name || form.last_name ? `${form.first_name} ${form.last_name}` : "Spécialiste sans nom"}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100" data-testid="drawer-close">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Identité */}
          <section className="grid grid-cols-2 gap-3">
            <Field label="Prénom" required>
              <Input data-testid="f-first-name" value={form.first_name} onChange={e => set("first_name", e.target.value)} />
            </Field>
            <Field label="Nom" required>
              <Input data-testid="f-last-name" value={form.last_name} onChange={e => set("last_name", e.target.value)} />
            </Field>
            <Field label="Profession" required>
              <select data-testid="f-profession" value={form.profession} onChange={e => set("profession", e.target.value)}
                className="w-full h-9 rounded-lg border border-slate-300 px-3 text-sm bg-white">
                {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Barreau / Ordre professionnel">
              <Input data-testid="f-barreau" value={form.barreau} onChange={e => set("barreau", e.target.value)} />
            </Field>
            <Field label="Ville principale" required>
              <Input data-testid="f-city" value={form.city} onChange={e => set("city", e.target.value)} />
            </Field>
            <Field label="Zone d'intervention" hint="Séparées par ;">
              <Input data-testid="f-zone" value={form.zone_intervention} onChange={e => set("zone_intervention", e.target.value)} placeholder="Paris; IDF; National" />
            </Field>
          </section>

          {/* Spécialités */}
          <section className="grid grid-cols-1 gap-3">
            <Field label="Spécialités principales" hint="Séparées par ;">
              <Input data-testid="f-specialties" value={form.specialties} onChange={e => set("specialties", e.target.value)} placeholder="Droit des affaires; M&A; Fiscalité" />
            </Field>
            <Field label="Sous-spécialités" hint="Séparées par ;">
              <Input data-testid="f-sub-specialties" value={form.sub_specialties} onChange={e => set("sub_specialties", e.target.value)} placeholder="Pacte d'actionnaires; Due diligence" />
            </Field>
          </section>

          {/* Présentation */}
          <section className="space-y-3">
            <Field label="Présentation courte" hint={`${form.presentation_courte.length}/300`}>
              <textarea data-testid="f-pres-courte" value={form.presentation_courte} onChange={e => set("presentation_courte", e.target.value.slice(0, 300))}
                rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </Field>
            <Field label="Présentation détaillée">
              <textarea data-testid="f-pres-detaillee" value={form.presentation_detaillee} onChange={e => set("presentation_detaillee", e.target.value)}
                rows={5} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </Field>
          </section>

          {/* Contact */}
          <section className="grid grid-cols-2 gap-3">
            <Field label="Téléphone">
              <Input data-testid="f-phone" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+33 1 …" />
            </Field>
            <Field label="Email">
              <Input data-testid="f-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} />
            </Field>
            <Field label="Site internet">
              <Input data-testid="f-website" value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Langues parlées" hint="Séparées par ;">
              <Input data-testid="f-languages" value={form.languages} onChange={e => set("languages", e.target.value)} placeholder="Français; Anglais" />
            </Field>
            <Field label="Années d'expérience">
              <Input data-testid="f-experience" type="number" min="0" max="80" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} />
            </Field>
            <Field label="Diplômes / Certifications" hint="Séparés par ;">
              <Input data-testid="f-diplomas" value={form.diplomas} onChange={e => set("diplomas", e.target.value)} />
            </Field>
          </section>

          {/* Médias */}
          <section className="grid grid-cols-2 gap-3">
            <Field label="Photo (URL)">
              <Input data-testid="f-photo" value={form.photo} onChange={e => set("photo", e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Vidéo de présentation (URL)">
              <Input data-testid="f-video" value={form.presentation_video} onChange={e => set("presentation_video", e.target.value)} placeholder="https://…" />
            </Field>
          </section>

          {/* Statut Confilex */}
          <section className="rounded-lg bg-[#FAF7F1] border border-[#C9A961]/30 p-4 space-y-3">
            <h3 className="text-xs uppercase tracking-wider text-[#0B3A2A] font-medium flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Statut & visibilité CONFILEX
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Abonnement">
                <select data-testid="f-subscription" value={form.subscription_status} onChange={e => set("subscription_status", e.target.value)}
                  className="w-full h-9 rounded-lg border border-slate-300 px-3 text-sm bg-white">
                  <option value="active">Actif</option>
                  <option value="suspended">Suspendu</option>
                  <option value="pending">En attente</option>
                </select>
              </Field>
              <Field label="Disponibilité">
                <select data-testid="f-availability" value={form.availability} onChange={e => set("availability", e.target.value)}
                  className="w-full h-9 rounded-lg border border-slate-300 px-3 text-sm bg-white">
                  {AVAILABILITIES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="f-referenced" type="checkbox" checked={form.is_referenced} onChange={e => set("is_referenced", e.target.checked)} />
                <span>Spécialiste référencé</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="f-premium" type="checkbox" checked={form.is_premium} onChange={e => set("is_premium", e.target.checked)} />
                <span>Spécialiste premium (cadre doré)</span>
              </label>
              <Field label={`Priorité d'affichage : ${form.display_priority}`}>
                <input data-testid="f-priority" type="range" min="0" max="100" value={form.display_priority}
                  onChange={e => set("display_priority", e.target.value)} className="w-full" />
              </Field>
              <Field label={`Score admin (interne) : ${form.admin_score}`} hint="Invisible au public, pondère le tri (~20%)">
                <input data-testid="f-admin-score" type="range" min="1" max="100" value={form.admin_score}
                  onChange={e => set("admin_score", e.target.value)} className="w-full accent-[#C9A961]" />
              </Field>
            </div>
            <Field label="Notes internes admin">
              <textarea data-testid="f-admin-notes" value={form.admin_notes} onChange={e => set("admin_notes", e.target.value)}
                rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Visible uniquement par l'admin" />
            </Field>
          </section>

          <div className="flex items-start gap-2 text-[11px] text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <p>
              Cette fiche est une <strong>vitrine admin</strong> — aucun compte utilisateur n'est créé pour le spécialiste.
              Le score admin et les notes restent strictement internes.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="drawer-cancel">Annuler</Button>
          <Button onClick={onSave} disabled={saving || !form.first_name || !form.last_name || !form.city}
            data-testid="drawer-save" className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {mode === "create" ? "Créer la fiche" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}
